import React, { useState, useEffect, useImperativeHandle, forwardRef, useId } from 'react';
import ToggleSwitch from '.././ToggleSwitch';
import ScopeSelector from './ScopeSelector';
import type { ISmartAlertPreference } from '../../types/api-caller';
import Loading from '.././Loading';

export interface SmartAlertsSettingsHandle {
  buildPayload: () => ISmartAlertPreference;
  forceEnable: () => void;
}

interface SmartAlertsSettingsProps {
  initialPreference: ISmartAlertPreference | null;
  alertsEnabled: boolean;
  onDirty: () => void;
  onSmartEnabledChange: (enabled: boolean) => void;
}

const SmartAlertsSettings = forwardRef<SmartAlertsSettingsHandle, SmartAlertsSettingsProps>(
  ({ initialPreference, alertsEnabled, onDirty, onSmartEnabledChange }, ref) => {
    const [smartAlertsEnabled, setSmartAlertsEnabled] = useState<boolean>(true);
    const [scoreIncreaseAlertEnabled, setScoreIncreaseAlertEnabled] = useState<boolean>(true);
    const [scoreDecreaseAlertEnabled, setScoreDecreaseAlertEnabled] = useState<boolean>(false);
    const [excludeLowArticlesForSmartAlerts, setExcludeLowArticlesForSmartAlerts] = useState<boolean>(false);
    const [portfolioSmartAlertsEnabled, setPortfolioSmartAlertsEnabled] = useState<boolean>(true);
    const [allListsEnabledForSmart, setAllListsEnabledForSmart] = useState<boolean>(false);
    const [selectedListsForSmart, setSelectedListsForSmart] = useState<number[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const smartIncreaseId = useId();
    const smartDecreaseId = useId();
    const smartLowArticlesId = useId();

    useEffect(() => {
      setLoading(true);

      if (!initialPreference) {
        setSmartAlertsEnabled(true);
        setScoreIncreaseAlertEnabled(true);
        setScoreDecreaseAlertEnabled(false);
        setExcludeLowArticlesForSmartAlerts(false);
        setPortfolioSmartAlertsEnabled(true);
        setAllListsEnabledForSmart(false);
        setSelectedListsForSmart([]);
        setLoading(false);
        return;
      }

      setSmartAlertsEnabled(initialPreference.enabled);
      setScoreIncreaseAlertEnabled(initialPreference.score_increase_enabled ?? true);
      setScoreDecreaseAlertEnabled(initialPreference.score_decrease_enabled ?? false);
      setExcludeLowArticlesForSmartAlerts(initialPreference.exclude_low_articles ?? false);

      if (initialPreference.scope_type === 'PORTFOLIO') {
        setPortfolioSmartAlertsEnabled(true);
        setAllListsEnabledForSmart(false);
        setSelectedListsForSmart([]);
      } else if (initialPreference.scope_type === 'ALL_LISTS') {
        setPortfolioSmartAlertsEnabled(false);
        setAllListsEnabledForSmart(true);
        setSelectedListsForSmart([]);
      } else {
        setPortfolioSmartAlertsEnabled(false);
        setAllListsEnabledForSmart(false);
        setSelectedListsForSmart(initialPreference.list_ids || []);
      }

      setLoading(false);
    }, []);

    function handleSmartAlertsEnabledChange(checked: boolean): void {
      setSmartAlertsEnabled(checked);

      if (checked && !scoreIncreaseAlertEnabled && !scoreDecreaseAlertEnabled) {
        setScoreIncreaseAlertEnabled(true);
      }

      onSmartEnabledChange(checked);
      onDirty();
    }

    function handleExcludeLowArticlesForSmartAlertsChange(checked: boolean): void {
      setExcludeLowArticlesForSmartAlerts(checked);
      onDirty();
    }

    function handleScoreIncreaseAlertEnabledChange(checked: boolean): void {
      if (!checked && !scoreDecreaseAlertEnabled && smartAlertsEnabled) {
        return;
      }

      setScoreIncreaseAlertEnabled(checked);
      onDirty();
    }

    function handleScoreDecreaseAlertEnabledChange(checked: boolean): void {
      if (!checked && !scoreIncreaseAlertEnabled && smartAlertsEnabled) {
        return;
      }

      setScoreDecreaseAlertEnabled(checked);
      onDirty();
    }

    function handlePortfolioSmartAlertsEnabledChange(checked: boolean): void {
      if (!checked && !allListsEnabledForSmart && selectedListsForSmart.length === 0) {
        return;
      }

      setPortfolioSmartAlertsEnabled(checked);
      if (checked) {
        setAllListsEnabledForSmart(false);
        setSelectedListsForSmart([]);
      }
      onDirty();
    }

    const handleSmartAllListsChange = (checked: boolean) => {
      if (!checked && !portfolioSmartAlertsEnabled && selectedListsForSmart.length === 0) {
        return;
      }

      setAllListsEnabledForSmart(checked);
      if (checked) {
        setSelectedListsForSmart([]);
        setPortfolioSmartAlertsEnabled(false);
      }
      onDirty();
    };

    const handleSmartListsChange = (listId: number, checked: boolean, allListsChecked: boolean) => {
      if (allListsEnabledForSmart && !portfolioSmartAlertsEnabled) {
        setAllListsEnabledForSmart(false);
        setSelectedListsForSmart([listId]);
        setPortfolioSmartAlertsEnabled(false);
      } else {
        if (checked) {
          if (allListsChecked) {
            setAllListsEnabledForSmart(true);
            setSelectedListsForSmart([]);
            setPortfolioSmartAlertsEnabled(false);
          } else {
            setSelectedListsForSmart([...selectedListsForSmart, listId]);
            setPortfolioSmartAlertsEnabled(false);
            setAllListsEnabledForSmart(false);
          }
        } else {
          setSelectedListsForSmart(selectedListsForSmart.filter((l) => l !== listId));
        }
      }
      onDirty();
    };

    useImperativeHandle(ref, () => ({
      buildPayload(): ISmartAlertPreference {
        let scope_type: 'PORTFOLIO' | 'ALL_LISTS' | 'SELECTED_LISTS';
        let list_ids: number[] | undefined;

        if (portfolioSmartAlertsEnabled) {
          scope_type = 'PORTFOLIO';
          list_ids = [];
        } else if (allListsEnabledForSmart) {
          scope_type = 'ALL_LISTS';
          list_ids = [];
        } else {
          scope_type = 'SELECTED_LISTS';
          list_ids = selectedListsForSmart;
        }

        return {
          enabled: alertsEnabled && smartAlertsEnabled,
          scope_type,
          exclude_low_articles: excludeLowArticlesForSmartAlerts,
          score_increase_enabled: scoreIncreaseAlertEnabled,
          score_decrease_enabled: scoreDecreaseAlertEnabled,
          list_ids,
        };
      },

      forceEnable() {
        if (!scoreIncreaseAlertEnabled && !scoreDecreaseAlertEnabled) {
          setScoreIncreaseAlertEnabled(true);
        }

        setSmartAlertsEnabled(true);
        onSmartEnabledChange(true);
      },
    }));

    if (!alertsEnabled) {
      return null; // when top level alerts toggle is disabled
    }

    return (
      <>
        <hr />
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <div className="flex flex-row justify-between items-center gap-4">
              <h2 className="text-ow-primary text-xl font-bold">Smart Alerts</h2>
              {!loading && <ToggleSwitch checked={smartAlertsEnabled} onChange={handleSmartAlertsEnabledChange} />}
              {/* <ToggleSwitch checked={smartAlertsEnabled} onChange={handleSmartAlertsEnabledChange} /> */}
            </div>
            Get notified when there is a sudden significant change in the fss score for any company in your Portfolio or Lists.
          </div>
          {!loading && smartAlertsEnabled && (
            <div className="flex flex-row gap-2 justify-end items-start">
              <div className="flex-1 flex flex-col gap-2">
                <div className="flex gap-3 items-start py-0 items-center">
                  <input
                    id={smartIncreaseId}
                    type="checkbox"
                    checked={scoreIncreaseAlertEnabled}
                    onChange={(e) => handleScoreIncreaseAlertEnabledChange(e.target.checked)}
                    className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
                  />
                  <label htmlFor={smartIncreaseId} className="font-medium text-base text-black cursor-pointer self-center">
                    if FSS score increases
                  </label>
                </div>

                <div className="flex gap-3 items-start py-0 items-center">
                  <input
                    id={smartDecreaseId}
                    type="checkbox"
                    checked={scoreDecreaseAlertEnabled}
                    onChange={(e) => handleScoreDecreaseAlertEnabledChange(e.target.checked)}
                    className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
                  />
                  <label htmlFor={smartDecreaseId} className="font-medium text-base text-black cursor-pointer self-center">
                    if FSS score decreases
                  </label>
                </div>

                <div className="flex-1 grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-3 items-center">
                  <input
                    id={smartLowArticlesId}
                    type="checkbox"
                    checked={excludeLowArticlesForSmartAlerts}
                    onChange={(e) => handleExcludeLowArticlesForSmartAlertsChange(e.target.checked)}
                    className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer row-span-1"
                  />
                  <label htmlFor={smartLowArticlesId} className="font-medium text-base text-black cursor-pointer self-center">
                    Remove companies with ‘low article’ scores from emails
                  </label>
                  <div /> {/* empty cell for alignment */}
                  <p className="text-gray-400 text-sm col-start-2 pr-8 pt-1">
                    This will remove companies from your daily/weekly emails that have ‘low article’ volumes, which may dominate top 20
                    email notifications because they can have bigger swings in percentage score changes.
                  </p>
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <ScopeSelector
                  portfolioEnabled={portfolioSmartAlertsEnabled}
                  allListsEnabled={allListsEnabledForSmart}
                  selectedLists={selectedListsForSmart}
                  onPortfolioChange={handlePortfolioSmartAlertsEnabledChange}
                  onAllListsChange={handleSmartAllListsChange}
                  onListsChange={handleSmartListsChange}
                  sectionId="smart"
                />
              </div>
            </div>
          )}
          {loading && <Loading />}
        </div>
      </>
    );
  }
);

export default SmartAlertsSettings;
